<template lang="pug">
  yz-dialog(v-if="showDialog" v-model="showDialog" persistent scrollable max-width="550px")
    yz-card
      yz-dialog-normal-header(:title="$t('download_item', { item: itemName })" @close="onClose")

      yz-dialog-normal-content.pa-6
        // Loading state
        template(v-if="isLoading")
          div(style="width: 450px; margin: auto;")
            yz-skeleton-loader(type="article")

        // Error state
        template(v-else-if="isError")
          div(style="width: 450px; margin: auto;")
            yz-empty-state-card(icon="$yzi-alert-triangle" :title="$t('error_message.unable_to_load_data')" :description="errorMessage" border-color="yz-danger-500" icon-color="yz-danger-500" :action-label="$t('retry')" @action-click="fetchTemplates")

        // Main content
        template(v-else)
          div(style="width: 450px; margin: auto;")
            // Description
            .yz-medium-body-md.text-neutral-700 {{ $t('download_item_description', { item: itemName }) }}

            // Template selection section
            .mt-6
              .yz-medium-body-sm.text-neutral-500 {{ $t('item_templates', { item: $options.filters.capitalize(itemName) }) }}
              v-radio-group.my-2(v-model="selectedDocument" mandatory hide-details)
                template(v-for="document in documents")
                  v-radio.mb-4(:key="document.id" :value="document" color="neutral700")
                    template(v-slot:label)
                      .neutral700--text.ml-2
                        .yz-medium-body-md {{ document.name }}
                        .yz-regular-body-sm.text-neutral-600 {{ document.description }}

      yz-dialog-normal-actions
        yz-btn(size="lg" text :label="$t('cancel')" @click="onClose")
        yz-btn(size="lg" :label="$t('confirm')" :loading="isDownloading" :disabled="isLoading || isError" @click="onConfirm")
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean,
      default: false
    },
    invoices: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isLoading: true,
      isError: false,
      isDownloading: false,
      errorMessage: '',
      documents: [],
      selectedDocument: null
    }
  },
  async mounted() {
    await this.fetchTemplates()
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters({
      company: 'getCompany'
    }),
    showDialog: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      }
    },
    itemName() {
      return this.$t('receipt').toLowerCase()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    async fetchTemplates() {
      this.isLoading = true
      this.isError = false
      this.errorMessage = ''

      try {
        const params = {
          company: this.company.id,
          status: 'active',
          categories: 'receipt'
        }
        const { data } = await this.$axios.get('/document_templates', { params })

        if (data.count === 0) {
          // No templates found - redirect to template creation
          this.isError = true
          this.errorMessage = this.$t('error_message.create_item_first', { item: this.$t('receipt').toLowerCase() })
          await this.showMessage(['error', this.errorMessage])
          await this.$router.push('/admin/clinic/settings/document/templates')
          this.onClose()
          return
        }

        this.documents = data.results

        // Auto-select if only one template
        if (data.count === 1) {
          this.selectedDocument = data.results[0]
          // Auto-download immediately
          await this.onConfirm()
        } else {
          // Multiple templates - show selection UI
          this.selectedDocument = data.results[0] // Pre-select first one
        }
      } catch (error) {
        console.error('Error fetching templates:', error)
        this.isError = true
        this.errorMessage = this.$t('error_message.unable_to_complete_request')
        await this.showMessage(['error', this.errorMessage])
      } finally {
        this.isLoading = false
      }
    },
    onClose() {
      this.$emit('input', false)
      this.$emit('close')
    },
    async onConfirm() {
      if (!this.selectedDocument) {
        await this.showMessage(['error', this.$t('error_message.please_select_template')])
        return
      }

      if (!this.invoices || this.invoices.length === 0) {
        await this.showMessage(['error', 'No invoices selected'])
        return
      }

      this.isDownloading = true

      try {
        const leadPids = this.invoices.map(invoice => invoice.lead_pid).join(',')

        const params = {
          document_template: this.selectedDocument.id,
          ids: leadPids
        }

        // Download as blob to handle file download (PDF for single, ZIP for multiple)
        const response = await this.$axios.get('/invoices/download', {
          params,
          responseType: 'blob'
        })

        // Extract filename from content-disposition header
        let filename = `receipts_${Date.now()}.pdf`
        const contentDisposition = response.headers['content-disposition']
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/)
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/"/g, '')
          }
        }

        // Create blob and trigger download
        const blob = new Blob([response.data])
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = filename
        link.click()
        window.URL.revokeObjectURL(link.href)

        await this.showMessage(['success', this.$t('panel.download_started')])
        this.onClose()
      } catch (error) {
        console.error('Error downloading receipts:', error)
        await this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isDownloading = false
      }
    }
  }
}
</script>
